import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import * as Yup from 'yup';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import {
  Formik,
  Form,
  FieldArray,
  FormikProps,
  useFormikContext,
  useField,
  FieldArrayRenderProps,
} from 'formik';

import HelloModal from 'common/components/hello-modal';
import TooltipUI, {
  ArrowDirection,
} from 'hellospa/foundation/control/tooltip/tooltip-ui';
import HfReactHelper from 'common/utils/hf-react-helper';
import * as actions from 'hellospa/page/prep-and-send/data/actions';
import * as types from 'hellospa/page/prep-and-send/data/types';
import * as selectors from 'hellospa/page/prep-and-send/data/selectors';
import styles from '../index.scss';
import messages from './intl';
import AttachmentActiveIcon from 'hellospa/icons/attachment-icon';
import List from './list';
import Fields from './fields';
import Button, { ButtonType } from 'hellospa/foundation/control/button';

interface InitialValues {
  attachments: types.Attachment[],
  current: types.Attachment,
}

type Props = {
  parent: string,
  attachments: types.Attachment[],
  onClose: (attachments: types.Attachment[]) => void,
};

const autoGenPrefix = 'auto-gen-';
// New records are created automatically and have "auto-gen-" appended to their ID
const createAutoGenerated = (): types.Attachment => {
  const attachment = actions.generateAttachment();
  return {
    ...attachment,
    id: autoGenPrefix + attachment.id,
  };
};
const isAutoGenerated = ({ id }: types.Attachment) => id.includes(autoGenPrefix);

const Attachments = (props: Props) => {
  const {
    parent,
    onClose,
  } = props;

  const intl = useIntl();
  const attachmentEnabled = useSelector(selectors.allowRecipientAttachment);
  const [attachmentModalOpen, setAttachmentModalOpen] = useState(false);
  const [upsellModalOpen, setUpsellModalOpen] = useState(false);


  const pricingUrl = HfReactHelper.urlHelper('info/pricing', 'hellosign');

  const openAttachmentModal = () => setAttachmentModalOpen(true);
  const openUpsellModal = () => setUpsellModalOpen(true);
  const closeAttachmentModal = () => setAttachmentModalOpen(false);
  const closeUpsellModal = () => setUpsellModalOpen(false);

  const handleOpenModal = () => {
    if (attachmentEnabled) {
      openAttachmentModal();
    } else {
      openUpsellModal();
    }
  };

  const handleCloseModal = () => {
    if (attachmentEnabled) {
      closeAttachmentModal();
    } else {
      closeUpsellModal();
    }
  };

  const initialValues: InitialValues = {
    attachments: props.attachments,
    current: createAutoGenerated(),
  };

  const schema = Yup.object<InitialValues>({
    attachments: Yup.array().of(types.attachmentSchema)
      .unique(['name']),
    current: types.attachmentSchema,
  });

  return (
    <React.Fragment>
      <Button
        type="button"
        kind={ButtonType.Subtle}
        onClick={handleOpenModal}
        data-qa-ref={`${parent}.buttons.attachments.open`}
      >
        <AttachmentActiveIcon
          disabled={!attachmentEnabled}
          active={initialValues.attachments.length > 0}
          className={classnames(styles.attachmentIcon, {
            [styles.active]: initialValues.attachments.length > 0,
            [styles.disabled]: !attachmentEnabled,
          })} />
        <TooltipUI className={styles.tooltip} arrow={ArrowDirection.Bottom}>
          {intl.formatMessage(messages.btnOpenTooltip)}
        </TooltipUI>
      </Button>
      {attachmentModalOpen && (
        <Formik
          initialValues={initialValues}
          onSubmit={(values) => {
            onClose(values.attachments);
            handleCloseModal();
          }}
          validateOnChange={false}
          validationSchema={schema}
          // eslint-disable-next-line react/no-children-prop
          children={({ handleChange }) => (
            <FieldArray name="attachments" render={(attachmentsHelpers) => (
              <AttachmentModal
                attachmentsHelpers={attachmentsHelpers}
                handleChange={handleChange} />
            )} />
          )} />
      )}
      {upsellModalOpen && (
        <HelloModal
          isOpen={true}
          onRequestClose={handleCloseModal}
          buttons={[
            {
              type: 'primary',
              text: intl.formatMessage(messages.modalUpsellAttachment),
              buttonLink: pricingUrl,
            },
          ]}
          contentLabel={intl.formatMessage(messages.modalUpsellAttachment)}
        >
          {intl.formatMessage(messages.modalBodyUpsell)}
        </HelloModal>
      )}
    </React.Fragment>
  );
};

export default Attachments;

type AttachmentModalProps = {
  handleChange: FormikProps<InitialValues>['handleChange'],
  attachmentsHelpers: FieldArrayRenderProps,
}
function AttachmentModal({
  handleChange,
  attachmentsHelpers,
}: AttachmentModalProps) {
  const intl = useIntl();

  const { validateForm, handleSubmit } = useFormikContext();
  const [currentField, , currentHelper] = useField<types.Attachment>('current');
  const [attachmentsField] = useField<types.Attachment[]>('attachments');

  const isCurrentAutoGenerated = isAutoGenerated(currentField.value);

  const save = async () => {
    const hasErrors = await validateForm();

    if (Object.keys(hasErrors).length === 0) {
      // This was an auto-generated record, needs ID regenerated before adding to array
      if (isCurrentAutoGenerated) {
        const { id, ...payload } = currentField.value;
        attachmentsHelpers.push(actions.generateAttachment(payload));
      } else {
        const index = attachmentsField.value.findIndex(
          (attachment) => currentField.value.id === attachment.id,
        );
        attachmentsHelpers.replace(index, currentField.value);
      }

      currentHelper.setValue(createAutoGenerated());
      currentHelper.setTouched(false);
    }
  };

  const onClose = async () => {
    await save();

    currentHelper.setValue({
      ...createAutoGenerated(),
      // This needs a value that will pass validation.
      name: '_',
    });
    handleSubmit();
  };

  return (
    <HelloModal
      contentLabel={intl.formatMessage(messages.modalTitle)}
      width="540px"
      height="75vh"
      scrollable={true}
      isOpen={true}
      onRequestClose={onClose}
      buttons={[{
        type: 'primary',
        text: intl.formatMessage(messages.btnClose),
        'data-qa-ref': 'recipients.buttons.attachments.close',
        onClick: onClose,
      }]}
    >
      <Form
        onChange={(e) => {
          // Prevents parent's onChange from triggering,
          // which causes loss of field focus
          e.stopPropagation();
          return handleChange(e);
        }}
      >
        <p className="mb-3">
          {intl.formatMessage(messages.modalSubtext)}
        </p>

        <List
          createAutoGenerated={createAutoGenerated}
          formikHelpers={attachmentsHelpers} />

        <Fields
          save={save}
          isAutoGenerated={isCurrentAutoGenerated} />
      </Form>
    </HelloModal>

  );
}
